import { memo } from "react";
import { InputBase, ListItemText, MenuItem, Select, SelectChangeEvent, styled } from "@mui/material";
import styles from "./multiSelectInput.module.scss";
import iconArrowDown from "@source/assets/icons/iconArrowDown.svg";
import { ISelectItem } from "@components/UI/inputs/selectInput/selectInput";
import Checkbox from "@components/UI/buttons/checkbox/checkbox";

const ALL_SENTINEL = "__all__";

const BootstrapInput = styled(InputBase)(() => ({
  "& .MuiInputBase-input": {
    borderRadius: 8,
    backgroundColor: "#F8FAFF",
    border: "1px solid #DDE2F2",
    padding: "10px 16px",
    fontFamily: "Poppins",
    "&:focus": {
      borderColor: " #DDE2F2",
    },
  },
  "& .MuiSelect-icon": {
    top: "50%",
    transform: "translateY(-50%)",
  },
  "& .MuiSelect-iconOpen": {
    transform: "rotate(180deg) translateY(50%)",
  },
}));

export default memo((props: MultiSelectInputProps) => {
  const {
    value,
    onChange,
    items,
    id,
    placeholder,
    label,
    className,
    disabled,
    showSelectAll,
    selectAllLabel,
  } = props;

  const itemValues = items.map((i) => String(i.value));

  const handleChange = (e: SelectChangeEvent<typeof value>) => {
    const raw = e.target.value as string[];
    if (raw.includes(ALL_SENTINEL)) {
      if (value.length === itemValues.length && itemValues.length > 0) {
        onChange([]);
      } else {
        onChange([...itemValues]);
      }
      return;
    }
    onChange(raw);
  };

  const renderValue = (selected: string[]) => {
    if (!selected?.length) {
      return <span className={styles.valuePlaceholder}>{placeholder ?? ""}</span>;
    }
    const labels = selected
      .map((s) => items.find((item) => String(item.value) === s)?.title ?? s)
      .join(", ");
    return (
      <span className={styles.valueSelected} title={labels}>
        {labels}
      </span>
    );
  };

  const allSelected = itemValues.length > 0 && value.length === itemValues.length;
  const someSelected = value.length > 0 && !allSelected;

  return (
    <div
      className={`${styles.selectWrapper} ${className || ""} ${
        disabled ? styles.selectWrapper_disabled : ""
      }`}
    >
      {label ? <div className={styles.label}>{label}</div> : null}
      <Select
        className={styles.select}
        multiple
        value={value}
        id={id}
        name={id}
        variant="outlined"
        fullWidth
        disabled={disabled}
        displayEmpty
        input={<BootstrapInput />}
        IconComponent={(iconProps) => <img {...iconProps} src={iconArrowDown} alt="" />}
        renderValue={(selected) => renderValue(selected as string[])}
        onChange={handleChange}
        MenuProps={{ classes: { paper: styles.paper } }}
      >
        {showSelectAll && items.length > 0 ? (
          <MenuItem value={ALL_SENTINEL} className={styles.menuItem}>
            <Checkbox
              className={styles.menuCheckbox}
              checked={allSelected}
              indeterminate={someSelected}
              value={ALL_SENTINEL}
              onChange={() => {}}
              inputProps={{ readOnly: true }}
            />
            <ListItemText primary={selectAllLabel || "Select all"} />
          </MenuItem>
        ) : null}
        {items.map((item) => (
          <MenuItem key={item.id} value={String(item.value)} className={styles.menuItem}>
            <Checkbox
              className={styles.menuCheckbox}
              checked={value.indexOf(String(item.value)) > -1}
              value={String(item.value)}
              onChange={() => {}}
              inputProps={{ readOnly: true }}
            />
            <ListItemText primary={item.title} />
          </MenuItem>
        ))}
      </Select>
    </div>
  );
});

export interface MultiSelectInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  items: ISelectItem[];
  id: string;
  placeholder?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
  showSelectAll?: boolean;
  selectAllLabel?: string;
}
